/**
 * Login, as K3 «Kirish» draws it: the navy brand panel and the form column. Email and password,
 * and Telegram as the secondary path (an `AuthIdentity` the person made from their profile, §4).
 * The prototype's Google button is not here: Google is decided against for now (CLAUDE.md §11), and
 * a button that cannot sign anybody in is worse than none.
 */
import { useMutation } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useEffect, useState, type FormEvent, type JSX } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import type { TelegramWidgetUser } from "@/api/auth";
import { landingForUser } from "@/app/landing";
import { useTranslation } from "@/i18n";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { LANDING_LEAD_URL } from "@/lib/public-links";
import { useAuthStore, type Role } from "@/store/auth";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";

import { AUTH_FIELD, AUTH_INPUT, AUTH_LABEL, AUTH_LINK, AuthError, AuthFrame, AuthHeading } from "./AuthFrame";
import { AUTH_MESSAGES } from "./messages";
import { telegramSignInMessage, telegramSignInOutcome } from "./telegramOutcome";
import { signInWithTelegram } from "./telegramSession";
import { TelegramWidget } from "./TelegramWidget";
import { useAuthConfig } from "./useAuthConfig";

/** The demo accounts the seeder creates, so the roles can be compared side by side. */
const DEMO_ACCOUNTS: readonly { email: string; name: string; role: Role }[] = [
  { email: "nodira@ddtravel.uz", name: "Nodira Aliyeva", role: "operator" },
  { email: "jasur@ddtravel.uz", name: "Jasur Bekmurodov", role: "branch_manager" },
  { email: "madina@ddtravel.uz", name: "Madina Karimova", role: "moliyachi" },
  { email: "sardor@ddtravel.uz", name: "Sardor Alimov", role: "owner" },
];

const DEMO_PASSWORD = "demo12345";

/**
 * Whether to offer the one-click demo logins.
 *
 * Off unless the build explicitly asks for them. These accounts exist only where
 * `seed_demo` has run, and `seed_demo` refuses to run under production settings, so on
 * a real agency's login page every card would be a button that fails. Vite inlines this
 * at build time, so the production image does not contain the block or the addresses.
 */
const SHOW_DEMO_LOGINS = import.meta.env.VITE_SHOW_DEMO_LOGINS === "true";

/** Where the Telegram path stands. `waiting` is the POST in flight. */
type TelegramPhase = "idle" | "awaiting" | "waiting";

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstPassword = (useLocation().state as { firstPassword?: boolean } | null)?.firstPassword === true;
  const landing = (): string => landingForUser(useAuthStore.getState().user, firstPassword);
  const login = useAuthStore((state) => state.login);
  const pollChallenge = useAuthStore((state) => state.pollChallenge);
  // D9.9: a correct password with two-factor on waits for the person's own Telegram.
  const [challenge, setChallenge] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [telegramPhase, setTelegramPhase] = useState<TelegramPhase>("idle");
  const [telegramMessage, setTelegramMessage] = useState<{ text: string; tone: "danger" | "neutral" } | null>(
    null,
  );

  const { telegramBot } = useAuthConfig();

  const telegramSignIn = useMutation({
    mutationFn: (payload: TelegramWidgetUser) => signInWithTelegram(payload),
    onSuccess: () => {
      // Read the role back from the store, as the email path does, so a moliyachi lands
      // on Moliya.
      void navigate(landing(), { replace: true });
    },
    onError: (caught: unknown) => {
      const status = caught instanceof ApiError ? caught.status : null;
      const outcome = telegramSignInOutcome(status);
      setTelegramPhase("idle");
      setTelegramMessage({
        // A rejected request is never a success, so "success" here can only be a bug;
        // it still gets a visible message rather than a silent reset.
        text: outcome === "success" ? AUTH_MESSAGES.genericError : telegramSignInMessage(outcome),
        tone: "danger",
      });
    },
  });

  async function attempt(withEmail: string, withPassword: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const outcome = await login(withEmail, withPassword);
      if (outcome.kind === "two-factor") {
        setChallenge(outcome.challenge);
        return;
      }
      // Read the role back from the store: `login` is what resolves it, and a
      // moliyachi lands on Moliya rather than on Bosh sahifa.
      void navigate(landing(), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.status === 403 ? t("auth.noMembership") : t("auth.invalidCredentials"));
      } else {
        setError(t("common.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (challenge === null) return undefined;
    let stopped = false;
    const timer = window.setInterval(() => {
      void pollChallenge(challenge)
        .then((outcome) => {
          if (stopped || outcome === "pending") return;
          stopped = true;
          window.clearInterval(timer);
          if (outcome === "signed-in") {
            void navigate(landing(), { replace: true });
            return;
          }
          setChallenge(null);
          setError(outcome === "denied" ? t("auth.twoFactor.denied") : t("auth.twoFactor.expired"));
        })
        .catch(() => {
          // A dropped poll is retried on the next tick; the challenge lives three minutes.
        });
    }, 2000);
    return (): void => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [challenge, navigate, pollChallenge, t]);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void attempt(email, password);
  }

  function openTelegram(): void {
    setTelegramMessage(null);
    setTelegramPhase("awaiting");
  }

  function cancelTelegram(): void {
    setTelegramPhase("idle");
    // The widget reports nothing when someone backs out, so the cancel is the only
    // signal there is; say what happened rather than leave the panel silently gone.
    setTelegramMessage({ text: AUTH_MESSAGES.telegramDeclined, tone: "neutral" });
  }

  function onTelegramAuth(payload: TelegramWidgetUser): void {
    setTelegramPhase("waiting");
    setTelegramMessage(null);
    telegramSignIn.mutate(payload);
  }

  return (
    <AuthFrame>
      <AuthHeading title={t("auth.title")} lead={t("auth.lead")} />

      {challenge === null ? null : (
        <div role="status" className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4">
          <span className="flex items-center gap-2 text-base font-medium text-ink">
            <Send strokeWidth={1.5} aria-hidden="true" />
            {t("auth.twoFactor.title")}
          </span>
          <p className="text-sm text-ink-muted">{t("auth.twoFactor.body")}</p>
          <Button
            variant="secondary"
            onClick={() => {
              setChallenge(null);
            }}
          >
            {t("auth.twoFactor.cancel")}
          </Button>
        </div>
      )}

      <form onSubmit={onSubmit} className={challenge === null ? "flex flex-col gap-4.5" : "hidden"}>
        <label className={AUTH_FIELD}>
          <span className={AUTH_LABEL}>{t("auth.email")}</span>
          <Input
            type="email"
            autoComplete="email"
            className={AUTH_INPUT}
            placeholder={t("auth.emailPlaceholder")}
            value={email}
            invalid={error !== null}
            onChange={(event) => {
              setEmail(event.target.value);
              setError(null);
            }}
            required
          />
        </label>

        <div className={AUTH_FIELD}>
          <span className="flex items-baseline justify-between gap-3">
            <label htmlFor="login-password" className={AUTH_LABEL}>
              {t("auth.password")}
            </label>
            <Link to="/forgot-password" className={AUTH_LINK}>
              {t("auth.forgot")}
            </Link>
          </span>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            className={AUTH_INPUT}
            placeholder={t("auth.passwordPlaceholder")}
            value={password}
            invalid={error !== null}
            onChange={(event) => {
              setPassword(event.target.value);
              setError(null);
            }}
            required
          />
        </div>

        {error === null ? null : <AuthError>{error}</AuthError>}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          {busy ? t("auth.submitting") : t("auth.submit")}
        </Button>
      </form>

      {telegramBot === "" ? null : (
        <div className="flex flex-col gap-4.5">
          <div aria-hidden="true" className="flex items-center gap-2.5 text-xs text-ink-subtle">
            <span className="h-px flex-1 bg-line" />
            {AUTH_MESSAGES.orDivider}
            <span className="h-px flex-1 bg-line" />
          </div>

          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            loading={telegramPhase === "waiting"}
            disabled={telegramPhase === "waiting" || busy}
            aria-expanded={telegramPhase === "awaiting"}
            onClick={openTelegram}
          >
            <Send strokeWidth={1.5} aria-hidden="true" />
            {AUTH_MESSAGES.telegramButton}
          </Button>

          {telegramPhase === "awaiting" ? (
            <div className="flex flex-col items-center gap-2 rounded-control border border-line p-3">
              <TelegramWidget botUsername={telegramBot} callbackName="kapitanTelegramLogin" onAuth={onTelegramAuth} />
              <Button variant="ghost" size="sm" onClick={cancelTelegram}>
                {t("common.cancel")}
              </Button>
            </div>
          ) : null}

          {telegramMessage === null ? null : (
            <p
              role={telegramMessage.tone === "danger" ? "alert" : "status"}
              className={cn("text-center text-sm", telegramMessage.tone === "danger" ? "text-danger-ink" : "text-ink-muted")}
            >
              {telegramMessage.text}
            </p>
          )}
        </div>
      )}

      <span className="text-center text-xs text-ink-subtle">
        {t("auth.notConnected")}{" "}
        <a href={LANDING_LEAD_URL} className="text-accent-ink hover:text-accent-hover">
          {t("auth.applyLink")}
        </a>
      </span>

      {!SHOW_DEMO_LOGINS ? null : (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-ink-muted">Demo sifatida kirish</span>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                className="flex flex-col gap-0.5 rounded-control border border-line bg-surface px-3 py-2.5 text-left hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-60"
                disabled={busy}
                onClick={() => {
                  void attempt(account.email, DEMO_PASSWORD);
                }}
              >
                <strong className="text-base font-semibold text-ink">{account.name}</strong>
                <span className="text-sm text-accent-ink">{t(`role.${account.role}`)}</span>
                <span className="text-xs text-ink-muted">{t(`role.${account.role}.hint`)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthFrame>
  );
}
